// safetyPage/components/safetyAudit/SafetyAuditTable.js
// This is the component for the safety audit table on the safety page, holding all safety audits that are in draft status for users.

import { useState } from "react";
import { ContextMenu } from "../../../common/ContextMenu";
import { buildCanonicalSafetyFormPath } from "../../../../services/SafetyFormRouteUtils";
import { useTableSort } from "../../../landingPage/components/del_table/useTableSort";
import { SortableHeader } from "../../../landingPage/components/del_table/SortableHeader";
// Styles
import styles from "../shared/safety-table-common.module.css";

// Safety Audit Table
export const SafetyAuditTable = ({
  safetyAuditList,
  onRowSelect,
  onDoubleClick,
  onDelete,
  selectedSafetyAuditId,
}) => {
  // State for the context menu when user right clicks on a row
  const [contextMenu, setContextMenu] = useState(null);
  const { sortedItems, sortColumn, sortDirection, onSort } = useTableSort(safetyAuditList);

  const handleContextMenu = (e, safetyAudit) => {
    e.preventDefault();
    onRowSelect(safetyAudit);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      safetyAudit: safetyAudit,
    });
  };

  const getSafetyAuditUrl = (safetyAudit) => {
    return buildCanonicalSafetyFormPath({
      tab: "safety_audit",
      safetyAuditId: safetyAudit.id,
      action: "edit",
      open: true,
    });
  };

  const handleOpenInNewTab = (safetyAudit) => {
    const url = getSafetyAuditUrl(safetyAudit);
    window.open(window.location.origin + url, "_blank");
  };

  const handleCopyLink = (safetyAudit) => {
    const url = window.location.origin + getSafetyAuditUrl(safetyAudit);
    navigator.clipboard.writeText(url);
  };

  const getContextMenuOptions = (safetyAudit) => [
    {
      label: "Open in new tab",
      action: () => handleOpenInNewTab(safetyAudit),
    },
    {
      label: "Copy link",
      action: () => handleCopyLink(safetyAudit),
    },
    {
      label: "Delete",
      action: () => onDelete(safetyAudit.id),
    },
  ];

  return (
    <div className={styles.nearMissTableContainer} id="safetyAuditTable">
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {safetyAuditList.map((safetyAudit) => (
            <div
              key={`card-${safetyAudit.id}`}
              onClick={() => onRowSelect(safetyAudit)}
              onDoubleClick={() => onDoubleClick(safetyAudit)}
              onContextMenu={(e) => handleContextMenu(e, safetyAudit)}
              className={`${styles.card} ${
                safetyAudit.id === selectedSafetyAuditId
                  ? styles.selectedCard
                  : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Created Date:</span>
                  <span className={styles.value}>{safetyAudit.created_at}</span>
                </div>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Updated Date:</span>
                <span className={styles.value}>{safetyAudit.updated_at}</span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Audit Date/Time:</span>
                <span className={styles.value}>
                  {safetyAudit.audit_date} {safetyAudit.audit_time}
                </span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Project Number:</span>
                <span className={styles.value}>
                  {safetyAudit.project_number}
                </span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Project Name:</span>
                <span className={styles.value}>{safetyAudit.project_name}</span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Supervisor:</span>
                <span className={styles.value}>
                  {safetyAudit.supervisor_name}
                </span>
              </div>
              <div className={styles.dataRow}>
                <span className={styles.label}>Scope of Work:</span>
                <span className={styles.value}>
                  {safetyAudit.scope_of_work &&
                  safetyAudit.scope_of_work.length > 40
                    ? `${safetyAudit.scope_of_work.substring(0, 40)}...`
                    : safetyAudit.scope_of_work}
                </span>
              </div>
              <div className={styles.cardActions}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(safetyAudit.id);
                  }}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={`${styles.table} ${styles.nearMissTable}`}>
        <thead>
          <tr>
            <SortableHeader label="Created Date" column="created_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Updated Date" column="updated_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Audit Date" column="audit_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <th>Audit Time</th>
            <th>Project Number</th>
            <th>Project Name</th>
            <th>Supervisor</th>
            <th>Scope of Work</th>
            <th className={styles.deleteColumn}>Delete</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((safetyAudit) => (
            <tr
              key={`row-${safetyAudit.id}`}
              onClick={() => onRowSelect(safetyAudit)}
              onDoubleClick={() => onDoubleClick(safetyAudit)}
              onContextMenu={(e) => handleContextMenu(e, safetyAudit)}
              className={
                safetyAudit.id === selectedSafetyAuditId
                  ? styles.selectedRow
                  : ""
              }
            >
              <td title={safetyAudit.created_at}>{safetyAudit.created_at}</td>
              <td title={safetyAudit.updated_at}>{safetyAudit.updated_at}</td>
              <td title={safetyAudit.audit_date}>{safetyAudit.audit_date}</td>
              <td title={safetyAudit.audit_time}>{safetyAudit.audit_time}</td>
              <td title={safetyAudit.project_number}>
                {safetyAudit.project_number}
              </td>
              <td title={safetyAudit.project_name}>
                {safetyAudit.project_name}
              </td>
              <td title={safetyAudit.supervisor_name}>
                {safetyAudit.supervisor_name}
              </td>
              <td title={safetyAudit.scope_of_work}>
                {safetyAudit.scope_of_work &&
                safetyAudit.scope_of_work.length > 40
                  ? `${safetyAudit.scope_of_work.substring(0, 40)}...`
                  : safetyAudit.scope_of_work}
              </td>
              <td className={styles.deleteColumn}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(safetyAudit.id);
                  }}
                  className={styles.deleteButton}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={getContextMenuOptions(contextMenu.safetyAudit)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};
